import { BackgroundJobStatus, IBackgroundJob } from '@models/backgroundJobs/IBackgroundJob';

/** Сколько закончившаяся задача остаётся в разделе: видно завершение, дальше итог живёт уведомлением в ленте. */
export const RECENT_FINISHED_MS = 60_000;

/** Строка, у которой вышло окно показа, ещё столько держится с классом ухода — успевает схлопнуться. */
export const LEAVE_MS = 240;

export interface ITrackedJob {
    job: IBackgroundJob;
    /** Когда пришёл этот снимок (`Date.now()`): от него идёт секундомер идущей задачи. */
    receivedAt: number;
    /** Когда клиент узнал об окончании — от этого момента считается окно показа итога. */
    finishedAt?: number;
    /** Итог уже показало окно задачи в этой вкладке: строка итога в разделе была бы повтором. */
    shownInWindow?: boolean;
    /** Когда пользователь скрыл строку итога: она уходит из раздела и больше не возвращается. */
    dismissedAt?: number;
}

export interface BackgroundJobsState {
    /** `null` — ещё не знаем; `false` — механизм выключен на сервере (список ответил 404). */
    enabled: boolean | null;
    jobs: Record<string, ITrackedJob>;
}

export type BackgroundJobsAction =
    /** `requestedAt` — когда ушёл запрос: задачи, узнанные позже, ответ ещё не мог содержать. */
    | { type: 'listLoaded'; jobs: IBackgroundJob[]; requestedAt: number; now: number }
    | { type: 'jobLoaded'; job: IBackgroundJob; now: number }
    | { type: 'jobGone'; jobId: string }
    | { type: 'shownInWindow'; jobId: string }
    | { type: 'dismissed'; jobId: string; now: number }
    /** Убрать закончившиеся задачи, чьё окно показа вышло, кроме тех, что `keep` оставляет (их ждут подписчики). */
    | { type: 'pruned'; now: number; keep: (jobId: string) => boolean }
    | { type: 'disabled' }
    /** Механизм, сочтённый выключенным, ответил задачей: спросить список заново. */
    | { type: 'recheck' }
    | { type: 'reset' };

export const initialBackgroundJobsState: BackgroundJobsState = { enabled: null, jobs: {} };

const ACTIVE_STATUSES: ReadonlySet<BackgroundJobStatus> = new Set(['queued', 'running', 'committing']);

export const isJobActive = (job: Pick<IBackgroundJob, 'status'>): boolean => ACTIVE_STATUSES.has(job.status);

export const isJobFinished = (job: Pick<IBackgroundJob, 'status'>): boolean => !isJobActive(job);

/** Порядок статусов: ответ, который откатил бы задачу назад, — устаревший (запросы обгоняют друг друга). */
const statusRank = (status: BackgroundJobStatus): number =>
    status === 'queued' ? 0 : status === 'running' ? 1 : status === 'committing' ? 2 : 3;

/**
 * Момент окончания по часам клиента. Для задачи, которую клиент застал идущей, — момент, когда узнал; для уже
 * закончившейся — её `finishedAt`, но не позже «сейчас»: часы сервера могут спешить относительно клиента.
 */
const finishedMoment = (job: IBackgroundJob, previous: ITrackedJob | undefined, now: number): number | undefined => {
    if (isJobActive(job)) return undefined;
    if (previous?.finishedAt !== undefined) return previous.finishedAt;
    if (previous && isJobActive(previous.job)) return now;
    const server = job.finishedAt ? Date.parse(job.finishedAt) : NaN;
    return Number.isFinite(server) ? Math.min(server, now) : now;
};

/** Ключи объектов по порядку: ответы списка и задачи (и слияние с прежним) складывают поля в разном порядке. */
const sortKeys = (_key: string, value: unknown): unknown =>
    value && typeof value === 'object' && !Array.isArray(value)
        ? Object.fromEntries(Object.entries(value).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)))
        : value;

/** Ответ повторяет известное: время работы растёт само, его снимок отсчитывает секундомер раздела. */
const sameSnapshot = (a: IBackgroundJob, b: IBackgroundJob): boolean =>
    JSON.stringify({ ...a, elapsedMs: undefined }, sortKeys) ===
    JSON.stringify({ ...b, elapsedMs: undefined }, sortKeys);

/** Новое состояние задачи; прежний объект — если ответ ничего не изменил, `undefined` — если ответ устарел. */
const track = (previous: ITrackedJob | undefined, job: IBackgroundJob, now: number): ITrackedJob | undefined => {
    if (previous && statusRank(job.status) < statusRank(previous.job.status)) return undefined;

    // Место в очереди и обычная длительность приходят только в `GET jobs/{id}`; список их не знает — прежнее
    // точнее, чем ничего.
    let merged = job;
    if (job.status === 'queued' && job.queuePosition == null && previous?.job.status === 'queued') {
        merged = { ...merged, queuePosition: previous.job.queuePosition };
    }
    if (isJobActive(job) && job.expectedMs == null && previous?.job.expectedMs != null) {
        merged = { ...merged, expectedMs: previous.job.expectedMs };
    }
    // Замечания — тоже только в `GET jobs/{id}`: список несёт лишь их число.
    if (job.warnings == null && previous?.job.warnings != null && previous.job.status === job.status) {
        merged = { ...merged, warnings: previous.job.warnings };
    }
    if (previous && sameSnapshot(previous.job, merged)) return previous;

    return {
        job: merged,
        receivedAt: now,
        finishedAt: finishedMoment(job, previous, now),
        shownInWindow: previous?.shownInWindow,
        dismissedAt: previous?.dismissedAt,
    };
};

const isRecentlyFinished = (tracked: ITrackedJob, now: number): boolean =>
    tracked.finishedAt !== undefined && now - tracked.finishedAt < RECENT_FINISHED_MS + LEAVE_MS;

const isUnchanged = (state: BackgroundJobsState, jobs: Record<string, ITrackedJob>): boolean => {
    const ids = Object.keys(jobs);
    return ids.length === Object.keys(state.jobs).length && ids.every((jobId) => jobs[jobId] === state.jobs[jobId]);
};

export const backgroundJobsReducer = (
    state: BackgroundJobsState,
    action: BackgroundJobsAction,
): BackgroundJobsState => {
    switch (action.type) {
        case 'listLoaded': {
            const jobs: Record<string, ITrackedJob> = {};
            for (const job of action.jobs) {
                const tracked = track(state.jobs[job.jobId], job, action.now);
                jobs[job.jobId] = tracked ?? state.jobs[job.jobId];
            }
            // Списка нет у только что поставленной (ответ ушёл раньше), у увиденной в другом окне и у идущей, что не
            // вошла в предел списка: первую и последнюю держим (идущую дочитывает клиент), вторую показываем, пока не
            // выйдет окно итога.
            for (const [jobId, tracked] of Object.entries(state.jobs)) {
                if (jobs[jobId]) continue;
                if (
                    isJobActive(tracked.job) ||
                    tracked.receivedAt >= action.requestedAt ||
                    isRecentlyFinished(tracked, action.now)
                ) {
                    jobs[jobId] = tracked;
                }
            }
            if (state.enabled === true && isUnchanged(state, jobs)) return state;
            return { enabled: true, jobs };
        }
        case 'jobLoaded': {
            // Задача пришла — механизм включён, даже если список ещё не ответил (или упал).
            const enabled = state.enabled ?? true;
            const previous = state.jobs[action.job.jobId];
            const tracked = track(previous, action.job, action.now);
            if (!tracked || tracked === previous) return enabled === state.enabled ? state : { ...state, enabled };
            return { enabled, jobs: { ...state.jobs, [action.job.jobId]: tracked } };
        }
        case 'pruned': {
            const stale = Object.entries(state.jobs).filter(
                ([jobId, tracked]) =>
                    isJobFinished(tracked.job) && !isRecentlyFinished(tracked, action.now) && !action.keep(jobId),
            );
            if (stale.length === 0) return state;
            const jobs = { ...state.jobs };
            stale.forEach(([jobId]) => delete jobs[jobId]);
            return { ...state, jobs };
        }
        case 'jobGone': {
            if (!state.jobs[action.jobId]) return state;
            const jobs = { ...state.jobs };
            delete jobs[action.jobId];
            return { ...state, jobs };
        }
        case 'shownInWindow': {
            const tracked = state.jobs[action.jobId];
            if (!tracked || tracked.shownInWindow) return state;
            return { ...state, jobs: { ...state.jobs, [action.jobId]: { ...tracked, shownInWindow: true } } };
        }
        case 'dismissed': {
            const tracked = state.jobs[action.jobId];
            if (!tracked || isJobActive(tracked.job) || tracked.dismissedAt !== undefined) return state;
            return {
                ...state,
                jobs: { ...state.jobs, [action.jobId]: { ...tracked, dismissedAt: action.now } },
            };
        }
        case 'disabled':
            return { enabled: false, jobs: {} };
        case 'recheck':
            return state.enabled === false ? { ...state, enabled: null } : state;
        case 'reset':
            return initialBackgroundJobsState;
    }
};

/** Задачи, которые на этом переходе закончились, — их клиент застал идущими. */
export const findJustFinished = (previous: BackgroundJobsState, next: BackgroundJobsState): IBackgroundJob[] =>
    Object.values(next.jobs)
        .filter((tracked) => {
            const before = previous.jobs[tracked.job.jobId];
            return before !== undefined && isJobActive(before.job) && isJobFinished(tracked.job);
        })
        .map((tracked) => tracked.job);

export const selectActiveJobs = (state: BackgroundJobsState): IBackgroundJob[] =>
    Object.values(state.jobs)
        .filter((tracked) => isJobActive(tracked.job))
        .map((tracked) => tracked.job);

export const hasActiveJobs = (state: BackgroundJobsState): boolean =>
    Object.values(state.jobs).some((tracked) => isJobActive(tracked.job));

export interface IVisibleJob extends ITrackedJob {
    /** Окно показа вышло: строка уходит. */
    leaving: boolean;
}

const isDismissedGone = (tracked: ITrackedJob, now: number): boolean =>
    tracked.dismissedAt !== undefined && now - tracked.dismissedAt >= LEAVE_MS;

/**
 * Что показывает раздел: идущие и закончившиеся за последние {@link RECENT_FINISHED_MS}, кроме тех, чей итог уже
 * показало окно задачи, и скрытых пользователем (скрытая строка уходит за {@link LEAVE_MS}); новые сверху.
 */
export const selectVisibleJobs = (jobs: Record<string, ITrackedJob>, now: number): IVisibleJob[] =>
    Object.values(jobs)
        .filter(
            (tracked) =>
                isJobActive(tracked.job) ||
                (!tracked.shownInWindow && isRecentlyFinished(tracked, now) && !isDismissedGone(tracked, now)),
        )
        .map((tracked) => ({
            ...tracked,
            leaving:
                tracked.dismissedAt !== undefined ||
                (tracked.finishedAt !== undefined && now - tracked.finishedAt >= RECENT_FINISHED_MS),
        }))
        .sort((a, b) => Date.parse(b.job.createdAt) - Date.parse(a.job.createdAt));

/** Через сколько мс состав раздела сменится сам (строка начнёт или закончит уходить); `null` — не сменится. */
export const nextVisibleChangeIn = (jobs: Record<string, ITrackedJob>, now: number): number | null => {
    let next: number | null = null;
    for (const tracked of Object.values(jobs)) {
        if (tracked.finishedAt === undefined) continue;
        const boundaries = [
            tracked.finishedAt + RECENT_FINISHED_MS,
            tracked.finishedAt + RECENT_FINISHED_MS + LEAVE_MS,
        ];
        if (tracked.dismissedAt !== undefined) boundaries.push(tracked.dismissedAt + LEAVE_MS);
        for (const boundary of boundaries) {
            const left = boundary - now;
            if (left > 0 && (next === null || left < next)) next = left;
        }
    }
    return next;
};

/** Сколько шла работа на момент `now`: у идущей — от снимка сервера плюс прошедшее с его прихода. */
export const jobElapsedMs = (tracked: ITrackedJob, now: number): number | null => {
    const { job } = tracked;
    if (job.status === 'queued') return null;
    if (isJobFinished(job)) return job.elapsedMs ?? null;
    return (job.elapsedMs ?? 0) + Math.max(0, now - tracked.receivedAt);
};
